import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRedirect from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { REGISTRATION_PAGE, SHOP_CART_PAGE } from "@/settings/constant";
import ColorType from "@/static/assets/pod-covers/color.png";
import MonoType from "@/static/assets/pod-covers/monochrome.png";
import SpringType from "@/static/assets/pod-covers/spring-type.png";
import WirelessType from "@/static/assets/pod-covers/wireless-type.png";
import { Button, Divider, TextField as SdsTextField, Typography } from "@bookips/sds";
import Image from "next/image";
import { useRouter } from "next/router";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { fileUploadByPresignedUrl } from "src/api/file";
import { useDialogStore } from "src/store/useDialogStore";
import useSnackbar from "src/store/useSnackbar";
import RadioBox from "./RadioBox";
import SectionContainer from "./SectionContainer";
import SectionLabel from "./SectionLabel";
import TextField from "./TextField";
import TotalLabel from "./TotalLabel";
import useAddPodToCart from "./hooks/useAddPodToCart";
import useCreatePOD from "./hooks/useCreatePOD";
import useFileUpload from "./hooks/useFileUpload";
const colorTypes = [
    {
        label: "흑백 (30원)",
        src: MonoType,
        value: "흑백",
        price: 33
    },
    {
        label: "컬러 (90원)",
        src: ColorType,
        value: "컬러",
        price: 99
    }
];
const bindingTypes = [
    {
        label: "무선 제본 (1,800원)",
        src: WirelessType,
        value: "무선",
        price: 1800
    },
    {
        label: "스프링 제본 (2,500원)",
        src: SpringType,
        value: "와이어",
        price: 2500
    }
];
// 제본 방식별 페이지 제작 가능 범위 안내
const bindingNotices = [
    "무선 제본 : 1권 당 최소 40 ~ 최대 800 페이지까지 제작 가능 (600 페이지 권장)",
    "스프링 제본 : 1권 당 최소 1 ~ 최대 400 페이지까지 제작 가능 (300 페이지 권장)"
];
const uploadFileWithUrl = async (param)=>{
    let { data , clientName , type , uploadFunction  } = param;
    if (!data) return;
    const url = await uploadFunction({
        fileType: type,
        contentType: data.type,
        clientName,
        expiredInSeconds: 900
    });
    await fileUploadByPresignedUrl(url, data, clientName);
};
const StyleAndQuantity = ()=>{
    const router = useRouter();
    const { open , close  } = useDialogStore();
    const { isLoggedIn , user  } = useAuthStore();
    const { addSnackbar  } = useSnackbar();
    const { redirectToLogin , redirectTo  } = useRedirect();
    const { mutateAsync: fileUpload  } = useFileUpload();
    const { mutateAsync: createPOD  } = useCreatePOD();
    const { mutateAsync: addPodToCart  } = useAddPodToCart();
    const [isLoading, setIsLoading] = useState(false);
    const { control , register , formState: { errors  } , watch , handleSubmit , reset  } = useFormContext();
    const quantity = watch("quantity");
    const pdfPageCount = watch("pdfPageCount");
    const color = watch("color");
    const binding = watch("binding");
    const getColorPrice = (colorType)=>{
        var _colorTypes_find;
        return ((_colorTypes_find = colorTypes.find((color)=>color.value === colorType)) === null || _colorTypes_find === void 0 ? void 0 : _colorTypes_find.price) || 0;
    };
    const getBindingPrice = (bindingType)=>{
        var _bindingTypes_find;
        return ((_bindingTypes_find = bindingTypes.find((binding)=>binding.value === bindingType)) === null || _bindingTypes_find === void 0 ? void 0 : _bindingTypes_find.price) || 0;
    };
    const unitPrice = (pdfPageCount || 0) * getColorPrice(color) + getBindingPrice(binding);
    const totalPrice = unitPrice * (quantity || 1);
    const handleValidSubmit = async (data)=>{
        try {
            setIsLoading(true);
            const { pdfFile , pdfPageCount , coverImage , coverCustomImage  } = data;
            const pdfClientName = "pod_pdf_".concat(user.id, "_").concat(Date.now());
            const coverClientName = "pod_cover_".concat(user.id, "_").concat(Date.now());
            await Promise.all([
                // 1. pdf 파일 업로드
                uploadFileWithUrl({
                    data: pdfFile,
                    clientName: pdfClientName,
                    type: "pdf",
                    uploadFunction: fileUpload
                }),
                // 2. 표지 이미지 업로드
                uploadFileWithUrl({
                    data: coverCustomImage,
                    clientName: coverClientName,
                    type: "cover",
                    uploadFunction: fileUpload
                })
            ]);
            const { id  } = await createPOD({
                ...data,
                design: Number(coverImage),
                page: pdfPageCount,
                tempKey: pdfClientName,
                originalFileName: pdfFile.name,
                ...coverCustomImage && {
                    coverTempKey: coverClientName,
                    coverOriginalFileName: coverCustomImage.name
                }
            });
            await addPodToCart({
                podId: id,
                quantity: data.quantity
            });
            open({
                id: "pod-created",
                type: "confirm",
                props: {
                    title: "장바구니에 담았어요.",
                    confirmText: "장바구니 이동",
                    onConfirm: ()=>{
                        mixpanelEvent("Popup Button Clicked", {
                            "Button Name": "장바구니 담기",
                            "Button URL": "".concat(SHOP_CART_PAGE, "?currentCart=pod"),
                            "Popup Title": "장바구니에 담았어요.",
                            "Popup Type": "POD 장바구니 담기 완료"
                        });
                        router.push("".concat(SHOP_CART_PAGE, "?currentCart=pod"));
                    },
                    cancelText: "닫기",
                    onCancel: ()=>{
                        mixpanelEvent("Popup Button Clicked", {
                            "Button Name": "닫기",
                            "Button URL": null,
                            "Popup Title": "장바구니에 담았어요.",
                            "Popup Type": "POD 장바구니 담기 완료"
                        });
                        reset();
                        close("pod-created");
                        window.scrollTo({
                            top: 0
                        });
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            reset();
                            close("pod-created");
                            window.scrollTo({
                                top: 0
                            });
                        }
                    }
                }
            });
        } catch (error) {
            ;
        } finally{
            setIsLoading(false);
        }
    };
    const handleInvalidSubmit = (errors)=>{
        const hasRequiredError = Object.values(errors).some((error)=>{
            if ((error === null || error === void 0 ? void 0 : error.type) === "required") return true;
            return false;
        });
        if (hasRequiredError) {
            addSnackbar({
                message: "필수값을 모두 입력하세요.",
                variant: "error"
            });
        }
    };
    const handleAddToCart = (event)=>{
        mixpanelEvent("Product Added to Cart", {
            "Product Category": "POD"
        });
        if (!isLoggedIn) {
            open({
                id: "login-required",
                type: "confirm",
                props: {
                    title: "로그인이 필요해요.",
                    description: "로그인 또는 회원가입을 진행해주세요.",
                    confirmText: "회원가입",
                    cancelText: "로그인",
                    onConfirm: ()=>{
                        redirectTo(REGISTRATION_PAGE);
                    },
                    onCancel: ()=>{
                        redirectToLogin();
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            close("login-required");
                        }
                    }
                }
            });
            return;
        }
        handleSubmit(handleValidSubmit, handleInvalidSubmit)(event);
    };
    return /*#__PURE__*/ _jsxs(SectionContainer, {
        title: "제본 스타일 및 수량 설정",
        children: [
            /*#__PURE__*/ _jsx(SectionLabel, {
                title: "내지 컬러(VAT 별도)",
                children: /*#__PURE__*/ _jsx("ul", {
                    className: "grid grid-cols-2 gap-3",
                    children: colorTypes.map((color)=>/*#__PURE__*/ _jsx("li", {
                            children: /*#__PURE__*/ _jsx(RadioBox, {
                                label: /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col items-center justify-center gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(Image, {
                                            className: "w-[112px]",
                                            src: color.src,
                                            alt: color.label
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "body2",
                                            className: "text-primary",
                                            as: "span",
                                            children: color.label
                                        })
                                    ]
                                }),
                                value: color.value,
                                ...register("color")
                            })
                        }, color.label))
                })
            }),
            /*#__PURE__*/ _jsxs(SectionLabel, {
                title: "제본 방식",
                children: [
                    /*#__PURE__*/ _jsx("ul", {
                        className: "grid grid-cols-2 gap-3",
                        children: bindingTypes.map((binding)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(RadioBox, {
                                    label: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col items-center justify-center gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx(Image, {
                                                className: "h-[80px]! w-[56px] rounded-md",
                                                src: binding.src,
                                                alt: binding.label
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "body2",
                                                className: "text-primary",
                                                as: "span",
                                                children: binding.label
                                            })
                                        ]
                                    }),
                                    value: binding.value,
                                    ...register("binding")
                                })
                            }, binding.label))
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "pl-6",
                        children: bindingNotices.map((notice)=>/*#__PURE__*/ _jsx("li", {
                                className: "marker:text-secondary marker:text-sm list-item list-disc",
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "body2",
                                    className: "text-secondary",
                                    as: "span",
                                    children: notice
                                })
                            }, notice))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "요청 사항 (선택)",
                placeholder: "요청하고 싶은 사항을 적어주세요.",
                helperText: "200자 이하(공백 포함)로 작성해주세요.",
                maxLength: 200,
                ...register("message")
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative",
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "quantity",
                    rules: {
                        min: {
                            value: 1,
                            message: "1 ~ 1,000 사이로 작성해주세요."
                        },
                        max: {
                            value: 1000,
                            message: "1 ~ 1,000 사이로 작성해주세요."
                        }
                    },
                    render: (param)=>{
                        let { field  } = param;
                        var _errors_quantity;
                        /*#__PURE__*/ return _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SdsTextField, {
                                    type: "text",
                                    inputMode: "numeric",
                                    labelText: "제본 수량",
                                    size: "lg",
                                    classes: {
                                        input: "w-[100px]"
                                    },
                                    min: 1,
                                    max: 1000,
                                    ...field,
                                    error: !!errors.quantity,
                                    helperText: (_errors_quantity = errors.quantity) === null || _errors_quantity === void 0 ? void 0 : _errors_quantity.message,
                                    onChange: (e)=>{
                                        const { value  } = e.target;
                                        const numericValue = value.replace(/,/g, "");
                                        if (isNaN(Number(numericValue))) {
                                            return;
                                        }
                                        field.onChange(numericValue === "" ? 0 : Number(numericValue));
                                    },
                                    onBlur: (e)=>{
                                        const { value  } = e.target;
                                        const numericValue = value.replace(/,/g, "");
                                        const numValue = Number(numericValue);
                                        if (numericValue === "" || numValue === 0) {
                                            field.onChange(1);
                                        }
                                        field.onBlur();
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "subtitle1",
                                    className: "text-primary absolute top-[45px] left-[108px]",
                                    as: "span",
                                    children: "권"
                                })
                            ]
                        });
                    }
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-gray-0 flex flex-col rounded-md p-6",
                children: [
                    /*#__PURE__*/ _jsx(TotalLabel, {
                        label: "권당 단가",
                        value: "".concat(pdfPageCount ? unitPrice.toLocaleString() : "-", "원")
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "caption",
                        className: "text-gray-300",
                        as: "span",
                        children: "(PDF 페이지 수 X 내지 컬러) + 제본 방식"
                    }),
                    /*#__PURE__*/ _jsx(TotalLabel, {
                        label: "총 제본 수량",
                        value: "".concat((quantity || 1).toLocaleString(), "권"),
                        className: "mt-3"
                    }),
                    /*#__PURE__*/ _jsx(Divider, {
                        className: "mt-4"
                    }),
                    /*#__PURE__*/ _jsx(TotalLabel, {
                        label: "총 제본 금액",
                        bold: true,
                        value: "".concat(pdfPageCount ? totalPrice.toLocaleString() : "-", "원"),
                        className: "mt-4"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Button, {
                size: "lg",
                onClick: handleAddToCart,
                isLoading: isLoading,
                children: "장바구니 담기"
            })
        ]
    });
};
export default StyleAndQuantity;
